import React from "react";

import { planDetailsURL } from "QuotePage/urls";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import _ from "underscore";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";
import simplifyPlanName from "Utils/simplifyPlanName";

import { Table, TR, TH, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import { Card, CardHeader, CardSection } from "Common/Card";
import { CarrierLogo } from "Common/CarrierLogo";
import { isSampleQuote } from "Common/EnterAges/Selectors";
import { getPlans } from "Common/PlanLoader/Selectors";

import selectSimilarPlans from "./Utils/SelectSimilarPlans";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("SimilarPlans");
const highlightedColumnStyle = { backgroundColor: "#e5f0f7" };

type Props = {
  similarPlansStruct: Array<[MedicalPlanPrice, string]>;
  isSampleQuote: boolean;
};

export class SimilarPlansDumb extends React.Component<Props> {
  constructor(props: Props) {
    super(props);

    (this as any).handleOpenSimilarPlan = this.handleOpenSimilarPlan.bind(this);
  }

  render() {
    const { similarPlansStruct } = this.props;

    // The Similar Plans selector returns a struct that looks like this
    // [[PlanObject, "This Plan"], [PlanObject, "Lower Doctors Visits"], ...]
    const similarPlansHeaders = similarPlansStruct.map((sp) => sp[1]);
    const similarPlans = similarPlansStruct.map((sp) => sp[0]);

    return (
      <Card theme="flat" id="similarPlans" p={0} className={bem()}>
        <CardHeader>Compare Similar Plans</CardHeader>
        <CardSection pt={0}>
          <Table mt={4}>
            <thead>{this.renderTableHeader(similarPlans, similarPlansHeaders)}</thead>
            <tbody>{this.renderBodySection(similarPlans)}</tbody>
          </Table>
        </CardSection>
      </Card>
    );
  }

  handleOpenSimilarPlan(plan: MedicalPlanPrice) {
    AnalyticsTracking.trackEvent("plan_viewed", {
      plan_id: plan.id,
      name: plan.raw_name,
      brand: plan.carrier,
      total_cost: plan.total_cost,
      average_cost: plan.average_monthly_premium,
      network: plan.plan_type,
      deductible: plan.deductible,
      doctor_visits: plan.estimated_costs.pcp_visit,
      hospitalization: plan.estimated_costs.hospitalization,
      generic_drugs: plan.estimated_costs.generic_drugs,
      location: "similar_plans",
    });
  }

  renderTableHeader(similarPlans: Array<MedicalPlanPrice>, similarPlansHeaders: Array<string>) {
    // Inline styles to override Table CSS Specificity
    return [
      <TR key="plan_differences_row">
        <TD style={{ width: "16%" }} />
        {similarPlansHeaders.map((sp, i) => (
          <TH key={i}>{sp}</TH>
        ))}
      </TR>,
      <TR key="carrier_logo_row">
        <TD> Carrier </TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            <CarrierLogo plan={sp} maxSize={2} />
          </TD>
        ))}
      </TR>,
      <TR key="plan_name_link_row">
        <TD> Plan Name </TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            <Link to={planDetailsURL({ params: { planID: sp.id } })} onClick={() => this.handleOpenSimilarPlan(sp)}>
              {simplifyPlanName(sp)}
            </Link>
          </TD>
        ))}
      </TR>,
    ];
  }

  renderBodySection(similarPlans: Array<MedicalPlanPrice>) {
    return [
      <TR key="total_premium_row">
        <TD>Total Cost</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(this.getTotalCost(sp, this.props.isSampleQuote))}
            <BodyText componentClass="span" size="ns-twelve">
              /month
            </BodyText>
            <br />
            {/* Don't render the cost difference for 1st plan, which is current plan being shown */}
            {/* Don't render the cost difference if the total cost havn't loaded in yet, since costDifference needs to do MATH */}
            {i !== 0 &&
              !sp.prices_loading &&
              !similarPlans[0].prices_loading &&
              this.getCostDifference(this.getTotalCost(sp, this.props.isSampleQuote), this.getTotalCost(similarPlans[0], this.props.isSampleQuote))}
          </TD>
        ))}
      </TR>,
      <TR key="average_premium_row">
        <TD>Employee Average</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(sp.average_monthly_premium)}
            <BodyText componentClass="span" size="ns-twelve">
              /month
            </BodyText>
            <br />
            {/* Don't render the cost difference for 1st plan, which is current plan being shown */}
            {/* Don't render the cost difference if the costs havn't loaded in yet, since costDifference needs to do MATH. */}
            {i !== 0 &&
              !sp.prices_loading &&
              !similarPlans[0].prices_loading &&
              this.getCostDifference(sp.average_monthly_premium, similarPlans[0].average_monthly_premium)}
          </TD>
        ))}
      </TR>,
      <TR key="network_row">
        <TD>Network</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {sp.plan_type}
          </TD>
        ))}
      </TR>,
      <TR key="deductible_row">
        <TD>Deductible</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(sp.deductible)}
          </TD>
        ))}
      </TR>,
      <TR key="doctor_visits_row">
        <TD>Doctor Visits</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(sp.estimated_costs.pcp_visit)}
          </TD>
        ))}
      </TR>,
      <TR key="generic_drugs_row">
        <TD>Generic Drugs</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(sp.estimated_costs.generic_drugs)}
          </TD>
        ))}
      </TR>,
      <TR key="brand_drugs_row">
        <TD>Brand Drugs</TD>
        {similarPlans.map((sp, i) => (
          <TD key={i} style={i === 0 ? highlightedColumnStyle : undefined}>
            {formatCurrency(sp.estimated_costs.brand_drugs)}
          </TD>
        ))}
      </TR>,
    ];
  }

  getTotalCost(plan: MedicalPlanPrice, isSampleQuote: boolean): number {
    if (isSampleQuote) {
      return plan.average_monthly_premium;
    }
    return plan.total_cost;
  }

  getCostDifference(planCost: number, baseCost: number) {
    return (
      <BodyText componentClass="span" size="ns-twelve" muted italic>
        ({planCost > baseCost ? "+" : "-"}
        {`${formatCurrency(Math.abs(planCost - baseCost))}/month`})
      </BodyText>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  // The selector returns more than 4 plans.
  similarPlansStruct: selectSimilarPlans(ownProps.plan, getPlans(state)).slice(0, 4),

  isSampleQuote: isSampleQuote(state),
});

export default withRouter(connect(mapStateToProps)(SimilarPlansDumb));
